import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import RootStoreContext from '~/contexts/RootStoreContext';
import {
  selectors as commonSelectors,
  actions as commonActions
} from '~/domains/common';
import { getPath } from '~/utils/routes';
// modalActions provides openModal/closeModal actions used to control the modal system
import { actions as modalActions } from '~/domains/modal';
import {
  selectors as agentScannersSelectors,
  actions as agentScannersActions
} from '~/domains/agentScanners';
// DeleteConfirmModal is imported to access its modalType property, which is used
// to identify the modal when dispatching modalActions.openModal in onDeleteClick.
// This enables the scanner detail view to trigger the delete confirmation dialog.
import DeleteConfirmModal from '../ScanConfigurationActions/DeleteConfirmModal';
import ScannerDetail from './ScannerDetail';

const mapStateToProps = (state, { match: { params } }) => {
  const scanners = agentScannersSelectors.scanners(state);
  const scanner = scanners.find((s) => s.id === params.scannerId);
  const profile = commonSelectors.profile(state);
  const organizationId = profile?.organization?.id;

  return {
    scanner,
    scannerId: params.scannerId,
    organizationId,
    isFetching: agentScannersSelectors.isFetchingScanners(state),
    scannerRuns: agentScannersSelectors.scannerRuns(state),
    isFetchingScannerRuns: agentScannersSelectors.isFetchingScannerRuns(state),
    scannerRunsError: agentScannersSelectors.scannerRunsError(state)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Reloads the scanners list to get updated scanner state.
  // Used by the polling mechanism in ScannerDetail to refresh scanner information
  // when the scanner is in an active state (QUEUED, RUNNING, IMPORTING).
  loadScanners: (organizationId) =>
    dispatch(agentScannersActions.loadAgentScanners(organizationId)),
  loadScannerRuns: (organizationId, scannerId, page, size) =>
    dispatch(
      agentScannersActions.loadScannerRuns(
        organizationId,
        scannerId,
        page,
        size
      )
    ),
  // Clears scanner runs from Redux state. Called on component unmount to prevent
  // stale data from persisting when navigating between different scanner details.
  clearScannerRuns: () => dispatch(agentScannersActions.clearScannerRuns()),
  // Navigates to the edit scanner page with the specified scanner ID.
  onEditClick: (scannerId) => {
    const editPath = getPath('agentScannersEdit', { scannerId });

    ownProps.history.push(editPath);
  },
  // Triggers a manual scan run for the given scanner configuration.
  onRunNowClick: (scanner, organizationId) => {
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 3000; // 3 seconds minimum

    // Trigger the scan execution
    dispatch(
      agentScannersActions.runScanConfiguration(organizationId, scanner.id)
    ).then(() => {
      // Show info alert while running
      const runAlert = {
        type: 'info',
        message: `Your '${scanner.name}' is currently updating.`,
        isHTML: false
      };

      dispatch(commonActions.setAlert(runAlert));

      // Ensure alert is visible for at least MIN_DISPLAY_TIME
      const elapsed = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

      setTimeout(() => {
        dispatch(commonActions.clearAlert());
        // Reload the scanners to get updated scanner state (enables polling)
        // and scanner runs to get updated scan history
        if (organizationId && scanner.id) {
          dispatch(agentScannersActions.loadAgentScanners(organizationId));
          dispatch(
            agentScannersActions.loadScannerRuns(organizationId, scanner.id)
          );
        }
      }, remainingTime);
    });
  },
  // Aborts the currently running scan for the given scanner configuration.
  onAbortClick: (scanner, organizationId) => {
    const startTime = Date.now();
    const MIN_DISPLAY_TIME = 3000; // 3 seconds minimum

    // Trigger the abort scan action
    dispatch(agentScannersActions.abort(organizationId, scanner.id)).then(
      () => {
        // Show info alert while aborting
        const abortAlert = {
          type: 'info',
          message: `Your '${scanner.name}' scan is being aborted.`,
          isHTML: false
        };

        dispatch(commonActions.setAlert(abortAlert));

        // Ensure alert is visible for at least MIN_DISPLAY_TIME
        const elapsed = Date.now() - startTime;
        const remainingTime = Math.max(0, MIN_DISPLAY_TIME - elapsed);

        setTimeout(() => {
          dispatch(commonActions.clearAlert());
          // Reload the scanner runs after alert closes
          if (organizationId && scanner.id) {
            dispatch(
              agentScannersActions.loadScannerRuns(organizationId, scanner.id)
            );
          }
        }, remainingTime);
      }
    );
  },
  // Opens a confirmation modal to delete a scanner configuration.
  // When confirmed, the modal will delete the scanner config and its associated
  // connection from the backend, then refresh the scanners list.
  // navigateToList: true tells the modal to navigate to the scanners list after deletion.
  onDeleteClick: (scanConfig) => {
    if (!scanConfig.id) {
      // eslint-disable-next-line no-console
      console.error('scanConfigId is required for delete operation');

      return;
    }
    dispatch(
      modalActions.openModal({
        modalType: DeleteConfirmModal.modalType,
        modalProps: {
          scanConfig,
          navigateToList: true
        }
      })
    );
  }
});

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps, null, {
    context: RootStoreContext
  })
)(ScannerDetail);
